/**
 * this file contains all redux-saga watchers & workers for the fund
 * - watchers watch for dispatched actions
 * - workers invoke another actions in response to the processed actions
 *
 * NOTE: Saga's use the new ES6 generator functions function* ()
 * NOTE: Arrow-function can not be used as generators as the yield keyword
 * may not work in an arrow function's body
 *
 * Created by Paul on 7/5/19
 */

import { takeEvery, call, put, select } from 'redux-saga/effects'

import C from '../constants'
import config from '../config'
import Cookies from 'js-cookie'
import { request, isEmpty, substitute } from '../utility/utilities'
import {stopSubmit} from 'redux-form'

// import action creators
import * as FundActions from '../actions/fundActions'

/* -----------------------------------------------------------------------------
    WATCHER & WORKER SAGA TO FETCH THE FUND DATA
----------------------------------------------------------------------------- */

// watcher saga for creating a new fund
export function* watchCreateFund() {
    yield takeEvery(C.CREATE_NEW_FUND, workerCreateFund)
}

// worker saga for creating a new fund
function* workerCreateFund(action) {

    const fundData = action.payload

    // set the headers
    const headers = {
        method: 'POST',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            'X-CSRFToken': Cookies.get('csrftoken')
        },
        data: fundData,
        mode: 'cors',
        cache: 'default',
        body: JSON.stringify(fundData)
    }

    // set the endpoint
    const requestURL = config.endpoints.createFundURL

    try {
        // show the loader
        yield put(FundActions.showSubmittingFundLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {

            yield put(FundActions.setCreateFundForm(response.data))

            // Successfully created, move to fund structure form
            yield put(FundActions.setActiveStep(1))
        }

    } catch (error) {
        // set form validation error
        yield put(stopSubmit('fundForm', error))
    } finally {
        // hide the loader regardless
        yield put(FundActions.showSubmittingFundLoader(false))
    }
}

// watcher saga for creating a new fund
export function* watchFetchInProgressFund() {
    yield takeEvery(C.FETCH_IN_PROGRESS_FUND, workerFetchInProgressFund)
}

// worker saga for creating a new fund
function* workerFetchInProgressFund(action) {

    const member_id = action.payload

    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }

    // set the endpoint
    const requestURL = substitute(config.endpoints.fetchInProgressFundURL, {member_id: member_id})

    try {
        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            yield put(FundActions.setCreateFundForm(response.data))
        }

    } catch (error) {
        // set form validation error
        console.log(error)
    } finally {
        // hide the loader regardless
    }
}


// watcher saga for creating a new fund
export function* watchUpdateFund() {
    yield takeEvery(C.UPDATE_FUND, workerUpdateFund)
}

// worker saga for creating a new fund
function* workerUpdateFund(action) {

    const fundData = action.payload

    // set the headers
    const headers = {
        method: 'POST',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            'X-CSRFToken': Cookies.get('csrftoken')
        },
        data: fundData,
        mode: 'cors',
        cache: 'default',
        body: JSON.stringify(fundData)
    }

    // set the endpoint
    const requestURL = config.endpoints.updateFundURL
    
    try {
        // show the loader
        yield put(FundActions.showSubmittingFundLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            if (response.data === 'Submitted') {
                // All done, move to a completed step
                yield put(FundActions.setActiveStep(4))
            }
            else {
                // Review state
                yield put(FundActions.setActiveStep(3))
            }
        }

    } catch (error) {

        // Display first form with errors
        if (error.fund_name || error.jurisdiction || error.company_name) {
            yield put(FundActions.setActiveStep(1))
        }
        else if (error.members) {
            yield put(FundActions.setActiveStep(2))
        }

        // Stop form submission and return errors
        yield put(stopSubmit('fundForm', error))

    } finally {
        // hide the loader regardless
        yield put(FundActions.showSubmittingFundLoader(false))
    }
}


// watcher saga for fetching the fund data
export function* watchFetchFund() {
    yield takeEvery(C.FETCH_FUND_DATA, workerFetchFund)
}

// worker saga for fetching the fund data
function* workerFetchFund(action) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundFetchURL, {member_id: action.payload})

    try {
        // show the loader
        yield put(FundActions.showFundLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            
            // set the fund data
            yield put(FundActions.setFundInfoData(response.data))

            if (response.data.fund_code_name) {
                yield put(FundActions.getFundOverallPosition(response.data.fund_code_name))
            }
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        yield put(FundActions.showFundLoader(false))
    }
}

// watcher saga for fetching the funds overall summary
export function* watchFetchFundOverallSummary() {
    yield takeEvery(C.FETCH_FUND_OVERALL_POSITION, workerFetchFundOverallSummary)
}

// worker saga for fetching the fund overall summary
function* workerFetchFundOverallSummary(fund_code) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundOverallSummaryFetchURL, { fund_code: fund_code.payload })
    
    try {
        // show the loader
        yield put(FundActions.showFundOverallPositionLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        const detail = response.data.data.portfolio_asset_positions[0]

        if(!isEmpty(response.data)) {
            yield put(FundActions.setFundPosition(detail.overall_value))
            yield put(FundActions.setFundAssetSummary(detail.asset_class_detail_list))
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        yield put(FundActions.showFundOverallPositionLoader(false))
    }
}

// watcher saga for fetching the funds detailed asset position
export function* watchFetchDetailedAssetPosition() {
    yield takeEvery(C.FETCH_FUND_DETAILED_ASSET_POSITION, workerFetchDetailedAssetPosition)
}

// worker saga for fetching the funds detailed asset position
function* workerFetchDetailedAssetPosition(fund_code) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundDetailedAssetPositionFetchURL, { fund_code: fund_code.payload })
    
    try {
        // show the loader
        yield put(FundActions.showFundDetailedAssetPositionLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            yield put(FundActions.setFundDetailedAssetPosition(response.data))
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        yield put(FundActions.showFundDetailedAssetPositionLoader(false))
    }
}

// watcher saga for fetching the fund cash reconciliation report
export function* watchFetchCashReconciliation() {
    yield takeEvery(C.FETCH_FUND_CASH_RECONCILIATION, workerFetchCashReconciliation)
}

// worker saga for fetching the funds detailed asset position
function* workerFetchCashReconciliation(action) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundCashReconciliationFetchURL, { member_id: action.payload })
    
    try {
        // show the loader
        yield put(FundActions.showFundCashReconciliationLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            yield put(FundActions.setFundCashReconciliationData(response.data))
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        yield put(FundActions.showFundCashReconciliationLoader(false))
    }
}

// watcher saga for requesting business events
export function* watchRequestBusinessEvents() {
    yield takeEvery(C.REQUEST_BUSINESS_EVENTS, workerRequestBusinessEvents)
}

// worker saga for requesting business events
function* workerRequestBusinessEvents(fund_code) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundRequestClassDetailsFetchURL, {fund_code: fund_code.payload, request_type: 'business'})

    try {
        // get the response promise object
        const response = yield call(request, requestURL, headers)

    } catch (error) {
        console.log(error)
    }
}

// watcher saga for requesting corporate actions
export function* watchRequestCorporateActions() {
    yield takeEvery(C.REQUEST_CORPORATE_ACTIONS, workerRequestCorporateActions)
}

// worker saga for requesting business events
function* workerRequestCorporateActions(fund_code) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundRequestClassDetailsFetchURL, {fund_code: fund_code.payload, request_type: 'corporate'})

    try {
        // get the response promise object
        const response = yield call(request, requestURL, headers)

    } catch (error) {
        console.log(error)
    }
}


// watcher saga for fetching fund contributions
export function* watchFetchFundContributions() {
    yield takeEvery(C.FETCH_FUND_CONTRIBUTIONS, workerFetchFundContributions)
}

// worker saga for fetching the fund contributions
function* workerFetchFundContributions(action) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundContributionsFetchURL, { member_id: action.payload })
    
    try {
        // show the loader
        yield put(FundActions.showFundContributionsLoader(true))

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            yield put(FundActions.setFundContributionsData(response.data))
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        yield put(FundActions.showFundContributionsLoader(false))
    }
}

// watcher saga for fetching the fund summary (i.e. any outstanding events)
export function* watchFetchFundSummary() {
    yield takeEvery(C.FETCH_FUND_SUMMARY_DATA, workerFetchFundSummary)
}

// worker saga for fetching the fund summary
function* workerFetchFundSummary(action) {
    // set the headers
    const headers = {
        method: 'GET',
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        mode: 'cors',
        cache: 'default'
    }
    
    // set the endpoint
    const requestURL = substitute(config.endpoints.fundSummaryFetchURL, { member_id: action.payload })
    
    try {
        // show the loader
        

        // get the response promise object
        const response = yield call(request, requestURL, headers)

        if(!isEmpty(response.data)) {
            yield put(FundActions.setFundSummaryData(response.data))
        }

    } catch (error) {
        console.log(error)
    } finally {
        // hide the loader regardless
        
    }
}


// WEBPACK FOOTER //
// ./src/sagas/fundSaga.js